import { useEffect, useRef } from 'react';
import * as React from 'react';
import { observer } from 'mobx-react';
import { SERVER_URL } from 'Constants';
import { getMessageByKey } from 'Stores/LanguageStore';
import axios from 'axios';
import { getErrorMessages } from 'Util/GraphQLUtils';
import {
	getEventBookingWizardDataCacheToken,
	saveEventBookingWizardDataCacheToken,
} from 'Views/Components/_HumanWritten/BookingWizard/EventsBookingWizard/EventsBookingWizardData';
import {
	getBookingWizardDataCacheToken,
	saveBookingWizardDataCacheToken,
} from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardDataCacheToken';
import alert from '../../Util/ToastifyUtils';
import { store, IUserResult } from 'Models/Store';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import { RedirectLoader } from 'Views/Components/_HumanWritten/RedirectLoader/RedirectLoader';
import { getPendingVoucherPurchase } from 'Views/Components/_HumanWritten/PurchaseGiftCertificate/VoucherPurchasePendingFlag';
import { getPendingParkingPurchase } from 'Views/Components/_HumanWritten/PurchaseParking/ParkingPurchasePendingFlag';
import { getPendingAnytimeTicketPurchase } from 'Modules/AnytimeTicket/Utils/AnytimeTicketPurchasePendingFlag';
import { REGISTRATION_CONFIRM_PAGE_TITLE } from 'ConstantPageNames';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { trackVerifiedAccount } from 'Util/_HumanWritten/Analytics/analyticsEvents';

/**
 * Confirms user registration via email token
 *
 * Can take a location query param that will redirect after the confirmation occurs
 */
function RegistrationConfirmPage() {
	useEffect(() => {
		document.title = REGISTRATION_CONFIRM_PAGE_TITLE;
	}, []);

	const location = useLocation();
	const [searchParams] = useSearchParams();

	const [routeToRedirect, setRouteToRedirect] = React.useState('/ferry-schedule');
	const [loading, setLoading] = React.useState(false);
	const [confirmed, setConfirmed] = React.useState(false);

	const token = searchParams.get('token');
	const username = searchParams.get('username');

	// Prevent double-execution in React 18 Strict Mode
	const hasConfirmedRef = useRef(false);

	React.useEffect(() => {
		if (!token || !username) return;
		if (hasConfirmedRef.current) return;
		hasConfirmedRef.current = true;

		const confirmEmailData = {
			token,
			email: username,
		};

		// Automatically continue with registration
		setLoading(true);
		axios
			.post<IUserResult>(`${SERVER_URL}/api/register/confirm-email`, confirmEmailData)
			.then(response => {
				setLoading(false);
				setConfirmed(true);
				store.setLoggedInUser(response.data);
				trackVerifiedAccount();
				const id = searchParams.get('id');
				if (isNotNullOrUndefined(id) && id !== '') {
					if (getEventBookingWizardDataCacheToken() !== null) {
						saveEventBookingWizardDataCacheToken(id.toString());
					} else {
						saveBookingWizardDataCacheToken(id.toString());
					}
				}
			})
			.catch(response => {
				setLoading(false);
				hasConfirmedRef.current = false; // Allow retry on error
				const errorMessages = getErrorMessages(response).map((error: any) => (
					<p key={error.message}>{error.message}</p>
				));
				alert(
					<div>
						<h6>Email Confirmation was not successful</h6>
						{errorMessages}
					</div>,
					'error',
				);
			});
	}, [token, username, searchParams, location.pathname]);

	if (confirmed) {
		let subtitle = getMessageByKey('account-verification__success__label', 'An exciting adventure awaits...');
		let redirectRoute = routeToRedirect;

		if (getBookingWizardDataCacheToken() !== null) {
			redirectRoute = '/booking-wizard/tickets';
			subtitle = getMessageByKey(
				'account-verification__success__redirect-to-booking-wizard__label',
				'Taking you back to where you left off...',
			);
		}
		if (getEventBookingWizardDataCacheToken() !== null) {
			redirectRoute = '/event-booking-wizard/tickets';
			subtitle = getMessageByKey(
				'account-verification__success__redirect-to-booking-wizard__label',
				'Taking you back to where you left off...',
			);
		}
		if (getPendingVoucherPurchase()) {
			redirectRoute = '/home';
			subtitle = getMessageByKey(
				'account-verification__success__redirect-to-booking-wizard__label',
				'Taking you back to where you left off...',
			);
		}
		if (getPendingParkingPurchase()) {
			redirectRoute = '/home';
			subtitle = getMessageByKey(
				'account-verification__success__redirect-to-booking-wizard__label',
				'Taking you back to where you left off...',
			);
		}
		if (getPendingAnytimeTicketPurchase()) {
			redirectRoute = '/home';
			subtitle = getMessageByKey(
				'account-verification__success__redirect-to-booking-wizard__label',
				'Taking you back to where you left off...',
			);
		}

		return (
			<RedirectLoader
				title={getMessageByKey('account-verification__success__title', 'Good to go!')}
				subtitle={subtitle}
				to={redirectRoute}
			/>
		);
	}

	return <></>;
}

export default observer(RegistrationConfirmPage);
